import { FC, memo } from 'react'
import Map, { Layer, Source } from 'react-map-gl/maplibre'
import { useMapStyle } from '@widgets/Map/providers/MapLibre/useMapStyle'

import { MapProviderProps } from '../../lib/types'

import { useMapLibre } from './useMapLibre'

import 'maplibre-gl/dist/maplibre-gl.css'

/**
 * Мемоизированный контент карты
 * Не перерисовывается при изменении props родителя
 */
const MapLibreContent = memo<{
  mapRef: any
  initialViewState: any
  markersGeoJSON: any
  maxBounds?: [[number, number], [number, number]]
  onMapLoad: () => void
  mapStyle: any
}>(({ mapRef, initialViewState, markersGeoJSON, maxBounds, onMapLoad, mapStyle }) => {
  return (
    <Map
      ref={mapRef}
      initialViewState={initialViewState}
      mapStyle={mapStyle}
      maxBounds={maxBounds} // Ограничение области панорамирования
      style={{ width: '100%', height: '100%' }}
      onLoad={onMapLoad}
    >
      {/* Слой для динамических маркеров */}
      <Source data={markersGeoJSON} id='dynamic-markers' type='geojson'>
        {/* Круглые маркеры */}
        <Layer
          id='markers-circles'
          type='circle'
          paint={{
            'circle-radius': 8,
            'circle-color': ['get', 'color'],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
          }}
        />

        {/* Подписи маркеров */}
        <Layer
          id='markers-labels'
          type='symbol'
          layout={{
            'text-field': ['get', 'title'],
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
            'text-offset': [0, 1.5],
            'text-size': 12,
            'text-anchor': 'top',
          }}
          paint={{
            'text-color': '#000000',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1,
          }}
        />
      </Source>
    </Map>
  )
})

MapLibreContent.displayName = 'MapLibreContent'

/**
 * Провайдер карты MapLibre
 * Полностью совместим с MapAPI интерфейсом
 *
 * Адаптирует декларативный подход MapLibre (Source/Layer) к императивному MapAPI
 */
export const MapLibreProvider: FC<MapProviderProps> = ({ settings, onReady, onError }) => {
  const { mapRef, markersGeoJSON, onMapLoad, isInitialized } = useMapLibre(
    settings,
    onReady,
    onError
  )

  const { style: mapStyle, isLoading: isStyleLoading } = useMapStyle()

  const initialViewState = {
    longitude: settings.center[0],
    latitude: settings.center[1],
    zoom: settings.zoom,
  }

  console.log('🗺️ [MapLibreProvider] Рендер:', {
    isInitialized,
    markersCount: markersGeoJSON.features.length,
  })

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <MapLibreContent
        initialViewState={initialViewState}
        mapRef={mapRef}
        mapStyle={mapStyle}
        markersGeoJSON={markersGeoJSON}
        maxBounds={settings.maxBounds}
        onMapLoad={onMapLoad}
      />
    </div>
  )
}

MapLibreProvider.displayName = 'MapLibreProvider'
